import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { fileUrl, humanSize } from "../lib/apiExtra";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Send, Paperclip, FileText, Loader2, UserPlus } from "lucide-react";

// scope: "global" | "order"; orderId required when scope==="order"
export default function ChatPanel({ scope, orderId, height = "h-[520px]" }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const base = scope === "order" ? `/chat/order/${orderId}` : "/chat/global";
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const lastRef = useRef(null);
  const bottomRef = useRef(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const after = lastRef.current;
      const { data } = await api.get(base + (after ? `?after=${encodeURIComponent(after)}` : ""));
      if (data.length) {
        setMessages((prev) => {
          const seen = new Set(prev.map((m) => m.id));
          return [...prev, ...data.filter((m) => !seen.has(m.id))];
        });
        lastRef.current = data[data.length - 1].created_at;
      }
      try {
        const fd = new FormData();
        fd.append("scope", scope);
        if (orderId) fd.append("order_id", orderId);
        await api.post("/chat/read", fd);
      } catch {}
    } catch {}
  };

  useEffect(() => {
    setMessages([]); lastRef.current = null;
    load();
    const t = setInterval(load, 3000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [base]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async (e) => {
    e.preventDefault();
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      if (file) fd.append("file", file);
      await api.post(base, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setText(""); setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      await load();
    } catch {}
    finally { setSending(false); }
  };

  const fmt = (iso) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  const claim = async (m) => {
    try {
      await api.post(`/orders/${m.order_id}/claim`);
      toast.success(`Order ${m.order_ref || ""} assigned to you`);
      navigate(`/orders/${m.order_id}`);
    } catch (e) {
      if (e.response?.status === 409) { toast.message(e.response.data.detail); navigate(`/orders/${m.order_id}`); }
      else toast.error("Could not claim order");
    }
  };

  return (
    <div className="flex flex-col rounded-lg border border-border bg-card overflow-hidden">
      <div className={`flex-1 overflow-y-auto p-4 space-y-3 ${height}`} data-testid="chat-messages">
        {messages.length === 0 && <div className="text-center text-sm text-slate-400 mt-6">No messages yet. Say hello 👋</div>}
        {messages.map((m) => {
          const mine = m.sender_id === user?.id;
          return (
            <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
              <div className={`max-w-[75%] rounded-2xl px-3 py-2 ${mine ? "bg-primary text-white" : "bg-white/10 text-slate-100"}`}>
                {!mine && (
                  <div className="text-[11px] font-semibold mb-0.5 opacity-80">
                    {m.sender_name} <span className="uppercase tracking-wide opacity-70">· {m.sender_role}</span>
                  </div>
                )}
                {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                {m.msg_type === "new_order" && m.order_id && (
                  <button data-testid={`claim-order-btn-${m.order_id}`} onClick={() => claim(m)}
                    className="mt-1.5 inline-flex items-center gap-1.5 rounded-md bg-emerald-600 px-2.5 py-1 text-xs font-semibold text-white hover:bg-emerald-700 transition-colors">
                    <UserPlus className="h-3.5 w-3.5" /> Assign to me &amp; open
                  </button>
                )}
                {m.attachment && (
                  <a href={fileUrl(m.attachment.url)} target="_blank" rel="noopener noreferrer"
                    className={`mt-1 flex items-center gap-2 rounded-md border px-2 py-1 text-xs ${mine ? "border-white/40 text-white" : "border-white/15 text-slate-300 bg-card"}`}>
                    <FileText className="h-3.5 w-3.5" /> {m.attachment.name}
                    <span className="opacity-70">{humanSize(m.attachment.size)}</span>
                  </a>
                )}
                <div className={`text-[10px] mt-0.5 ${mine ? "text-white/70" : "text-slate-400"}`}>{fmt(m.created_at)}</div>
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>
      <form onSubmit={send} className="border-t border-border p-3 flex items-center gap-2">
        <button type="button" onClick={() => fileRef.current?.click()} data-testid="chat-attach-btn"
          className="rounded-md border border-white/15 p-2 text-slate-400 hover:bg-white/5 transition-colors">
          <Paperclip className="h-4 w-4" />
        </button>
        <input ref={fileRef} type="file" className="hidden" data-testid="chat-file-input" onChange={(e) => setFile(e.target.files[0])} />
        <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)}
          placeholder={file ? `Attach: ${file.name}` : "Type a message…"}
          className="flex-1 rounded-md border border-white/15 px-3 py-2 text-sm focus:ring-2 focus:ring-teal focus:outline-none" />
        <button type="submit" data-testid="chat-send-btn" disabled={sending}
          className="inline-flex items-center gap-1.5 rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-navy-2 transition-colors disabled:opacity-50">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </button>
      </form>
    </div>
  );
}
